import {
  DndContext,
  closestCenter,
  MouseSensor,
  KeyboardSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToVerticalAxis,
  restrictToWindowEdges,
} from "@dnd-kit/modifiers";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CommandStopInput } from "./command";
import { t } from "i18next";
import { cn } from "@/lib/utils";
import { StopModel } from "@/models/stop.model";
import { useEffect, useRef } from "react";

const SortableItem = ({
  id,
  index,
  stop,
  type,
  focused,
  className,
  onRemove,
  onInputChange,
  onInputFocus,
  onInputBlur,
}: {
  id: string;
  index: number;
  stop: StopModel;
  type?: "input" | "command";
  focused: boolean;
  className?: string;
  onRemove: (index: number) => void;
  onInputChange: (search: string, index: number) => void;
  onInputFocus: (index: number) => void;
  onInputBlur: () => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (focused) {
      inputRef.current?.focus();
    }
  }, [focused, stop]);

  return (
    <div
      className={cn("mb-1 kiosk:last:w-[88%]", className)}
      style={style}
      ref={setNodeRef}
      {...attributes}
    >
      <CommandStopInput
        listeners={listeners}
        placeholder={`${t("searchPlaceholder")}...`}
        value={stop.searchValue || ""}
        onChange={(e) => onInputChange(e.target.value, index)}
        onFocus={() => onInputFocus(index)}
        onBlur={onInputBlur}
        maxLength={20}
        index={index + 1}
        onRemove={() => onRemove(index)}
        floor={
          stop.poi?.floorName
            ? stop.poi.floorName
            : stop.feature?.properties?._dynamic?.floorName
              ? stop.feature?.properties?._dynamic.floorName
              : stop.feature?.properties?.level
        }
        className={cn("touch-manipulation", type === "input" && "h-[36px]")}
        containerClassname={cn(type === "input" && "!ml-0 !mr-0 bg-white px-4")}
        ref={inputRef}
      />
    </div>
  );
};

const StopsContext = ({
  stops,
  focusedStop,
  type,
  itemClassName,
  onRemove,
  onStopsChanged,
  onFocusChange,
}: {
  stops: StopModel[];
  focusedStop: number | null;
  type?: "input" | "command";
  itemClassName?: string;
  onRemove: (index: number) => void;
  onStopsChanged: (stops: StopModel[]) => void;
  onFocusChange: (index: number | null) => void;
}) => {
  // const [search, setSearch] = useState("");
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(KeyboardSensor),
    useSensor(TouchSensor),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = stops.findIndex((i) => i.id === active.id);
      const newIndex = stops.findIndex((i) => i.id === over.id);
      const updatedStops = arrayMove(stops, oldIndex, newIndex);
      onStopsChanged(updatedStops);
    }
  };

  const handleInputChange = (search: string, index: number) => {
    const updatedStops = [...stops];
    updatedStops[index].searchValue = search;
    updatedStops[index].poi = undefined;
    updatedStops[index].feature = undefined;
    onStopsChanged(updatedStops);
  };

  const handleFocus = (index: number) => {
    onFocusChange(index);
  };

  const handleBlur = () => {
    setTimeout(() => {
      onFocusChange(null);
    }, 500);
  };

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={stops} strategy={verticalListSortingStrategy}>
        <div className="flex w-full flex-col gap-1 lg:gap-2">
          {stops.map((stop, index) => (
            <SortableItem
              key={stop.id}
              id={stop.id}
              stop={stop}
              index={index}
              type={type}
              focused={index === focusedStop}
              onRemove={onRemove}
              onInputChange={handleInputChange}
              onInputFocus={handleFocus}
              onInputBlur={handleBlur}
              className={itemClassName}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default StopsContext;
